import { Calendar, Globe, Tag } from "lucide-react";

import { getItemTagName } from "@/modules/mine";
import type { Item } from "@/modules/mine/types";

import { formatDate } from "./preview-utils";

export type PreviewTab = "live" | "reader" | "snapshot";

export function WebsiteLivePreview({
  iframeKey,
  item,
  previewTab,
}: {
  iframeKey: number;
  item: Item;
  previewTab: PreviewTab;
}) {
  if (item.format !== "website" || previewTab !== "live") {
    return null;
  }

  return (
    <div className="group relative h-full w-full">
      <iframe
        key={iframeKey}
        src={item.url}
        className="h-full w-full border-none bg-white"
        title={item.title}
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
      />
      <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 opacity-0 transition-opacity group-hover:opacity-100">
        <div className="rounded-full bg-black/80 px-3 py-1.5 text-[10px] text-white backdrop-blur-sm">
          如果内容未显示，请点击上方“在新窗口打开”按钮
        </div>
      </div>
    </div>
  );
}

export function WebsiteSnapshotPreview({
  item,
  previewTab,
  sourceName,
}: {
  item: Item;
  previewTab: PreviewTab;
  sourceName: string;
}) {
  if (previewTab !== "snapshot") {
    return null;
  }

  return (
    <div className="mx-auto max-w-6xl p-4">
      <h1 className="mb-3 text-2xl font-bold leading-tight text-foreground">
        {item.title || "Untitled"}
      </h1>

      <div className="mb-4 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-1.5">
          {item.website?.favicon_url ? (
            <img src={item.website.favicon_url} className="size-4 rounded-full" />
          ) : (
            <Globe size={12} />
          )}
          <span>{sourceName}</span>
        </div>
        <div className="size-1 rounded-full bg-border" />
        <div className="flex items-center gap-1.5">
          <Calendar size={14} />
          <span>{formatDate(item.created_at)}</span>
        </div>
        <div className="size-1 rounded-full bg-border" />
        <div
          className={`flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${
            item.views === 0
              ? "border-primary/20 bg-card text-blue-500"
              : "border bg-muted/50 text-muted-foreground"
          }`}
        >
          {item.views === 0 ? "Unread" : "Read"}
        </div>
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        {item.tags?.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center gap-1 rounded-md bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground transition-colors hover:bg-secondary/80"
          >
            <Tag size={12} />
            {getItemTagName(tag)}
          </span>
        ))}
      </div>

      <div className="prose prose-sm mb-8 max-w-none leading-relaxed text-foreground/90 dark:prose-invert">
        <p>{item.note}</p>
      </div>
    </div>
  );
}
